import * as R from 'ramda';
import { createContext, useContext } from 'react';

/**
 * Context providing information about the size of the content area to its children.
 */ const AdaptiveHeightContext = createContext(null);
/**
 * Provider for {@link AdaptiveHeightContext}.
 */ const AdaptiveHeightProvider = AdaptiveHeightContext.Provider;
/**
 * Hook to access the {@link AdaptiveHeightContext} value.
 *
 * @param allowedTokens - List of allowed height tokens.
 */ function useAdaptiveHeightContext(allowedTokens) {
    const context = useContext(AdaptiveHeightContext);
    if (!context) return;
    if (!allowedTokens) return context;
    return findNearestAllowedHeight(context, allowedTokens);
}
/**
 * Returns the height token that's closest to the current height token and is within the list of
 * allowed height tokens.
 *
 * @template TAllowed - Inferred list of allowed height tokens.
 * @param height - The current height token.
 * @param allowedTokens - List of allowed height tokens. If omitted, all tokens are allowed.
 */ function findNearestAllowedHeight(height, allowedTokens) {
    const tokens = [
        '2xs',
        'xs',
        'sm',
        'md',
        'lg',
        'xl',
        '2xl'
    ];
    // Add 1 to index to include `currentHeight` in `smallerTokens`.
    const [smallerTokens, largerTokens] = R.splitAt(tokens.indexOf(height) + 1)(tokens);
    // Keeps the order of the last argument.
    const validSmallerTokens = R.intersection(allowedTokens)(smallerTokens);
    const validLargerTokens = R.intersection(allowedTokens)(largerTokens);
    const largestSmallerToken = R.nth(-1)(validSmallerTokens);
    const smallestLargerToken = R.nth(0)(validLargerTokens);
    return largestSmallerToken ?? smallestLargerToken;
}

export { AdaptiveHeightContext, AdaptiveHeightProvider, useAdaptiveHeightContext };
//# sourceMappingURL=adaptive-height-context.js.map
